// frontend/src/api/seasonApi.js
// Season league mode ("Mythic Dynasty") API client.
//
// Backend contract: AIDocs/Final/backend/api-reference-tdd.md §2.5a, served by
// backend/football/season/urls.py + views.py. Every endpoint below is real and
// implemented — there are no mocks in this file.
//
// TWO THINGS EVERY CALLER MUST KNOW:
//
// 1. EVERY season endpoint is gated behind the `football_season_league`
//    FeatureFlag, checked FIRST on every request. When the flag is DISABLED the
//    backend answers 403 with error_code SEASON_FEATURE_DISABLED. That is a
//    normal, expected answer for a user without access — callers should surface
//    it as "not available", not as a crash. Use `isSeasonFeatureDisabled(error)`.
//
// 2. Season failures use their OWN envelope: {error_code, message, context}.
//    The generic `errorData.message || HTTP <status>` pattern used elsewhere in
//    this codebase would throw away `error_code`, which IS the contract (the
//    messages are explicitly allowed to change). Everything here therefore
//    throws a `SeasonApiError` carrying the code, the context and the endpoint.
//
// 3. A 403 IS TWO DIFFERENT ANSWERS, and they are told apart by `error_code`,
//    never by the status:
//
//      403 WITH  error_code → a DOMAIN denial (SEASON_FEATURE_DISABLED,
//                             SEASON_NOT_COMMISSIONER…). The user is signed in
//                             and the system said no. Surface it; the session
//                             is fine.
//      401, or 403 WITHOUT  → NOT AUTHENTICATED. DRF's SessionAuthentication
//                             answers an expired session with its own generic
//                             `{"detail": …}` shape. This runs the shared
//                             `handleApiAuthError()` (clear cached auth →
//                             redirect to login) and throws
//                             errorCode 'NOT_AUTHENTICATED'.
//
//    Callers: use `isSeasonAuthExpired(error)` to suppress a Retry — retrying a
//    dead session just repeats the failure. Do NOT branch on status alone: that
//    would sign a user out every time a non-commissioner pressed a
//    commissioner-only button, which is an ordinary "no".
//
// NOT INCLUDED, DELIBERATELY:
//   · POST /api/v2/season-teams/<id>/packs/ — still 501 SEASON_NOT_IMPLEMENTED
//     (backend Phase 5). No wrapper exists so no caller can accidentally ship a
//     button for it.
//   · GET  /api/v2/season-teams/<id>/pack-offers/ — reads fine, but its only
//     purpose is feeding the unimplemented purchase above.

import { API_BASE, getCsrfHeaders } from './transport';
// Shared auth recovery — the SAME one every other API layer uses. Season must
// not invent its own session handling; see the isUnauthenticated branch below.
import { handleApiAuthError } from '../auth/authUtils';

/**
 * A season endpoint's structured failure.
 *
 * `errorCode` is the stable contract value (see
 * backend/football/season/error_codes.py). Branch on THAT, never on `message`.
 */
export class SeasonApiError extends Error {
    constructor({ errorCode, message, context, status, endpoint }) {
        // THE CONTEXT IS FOLDED INTO `message` ON PURPOSE.
        //
        // The season envelope puts the ACTIONABLE detail in `context` — most
        // importantly `context.validation_errors`, which names the exact field
        // and rule that failed. A bare `super(message)` left that invisible:
        // `console.error(error)` prints only `.message` + stack, so every
        // validation failure surfaced as the same useless "input failed
        // validation" line and the one piece of information that could fix it
        // was silently stranded on a property nobody logs.
        //
        // Both forms are kept: `.context` stays a structured object for code
        // that wants to branch on it, and the human-readable rendering rides
        // along in the message so it shows up in EVERY console.error, uncaught
        // rejection and error-boundary readout without the caller doing
        // anything.
        const contextDetail =
            context && Object.keys(context).length > 0
                ? ` | context: ${JSON.stringify(context)}`
                : '';
        super(`${message}${contextDetail}`);
        this.name = 'SeasonApiError';
        this.errorCode = errorCode;
        this.context = context || {};
        this.status = status;
        this.endpoint = endpoint;
        // The un-decorated message, for UI that wants to show the human
        // sentence without the JSON tail.
        this.baseMessage = message;
    }
}

/** True when the failure is simply "this user has no access to season mode". */
export function isSeasonFeatureDisabled(error) {
    return error instanceof SeasonApiError && error.errorCode === 'SEASON_FEATURE_DISABLED';
}

/**
 * True when the request failed because the SESSION is gone, not because the
 * domain said no.
 *
 * By the time a caller sees this, `handleApiAuthError()` has already cleared
 * cached auth and started the redirect to login — so a screen should NOT offer
 * a Retry for it. Retrying an expired session just repeats the same failure,
 * which is exactly the loop this distinction was added to break.
 */
export function isSeasonAuthExpired(error) {
    return error instanceof SeasonApiError && error.errorCode === 'NOT_AUTHENTICATED';
}

/**
 * One request against the season API.
 *
 * Never returns a partial/defaulted payload: a non-2xx becomes a SeasonApiError
 * and an unparseable body becomes a loud Error naming the endpoint. There is no
 * "return {} and let the page render empty" path — an empty league and a failed
 * read must never look the same.
 */
async function seasonRequest(path, { method = 'GET', body } = {}) {
    const endpoint = `${API_BASE}/v2/${path}`;
    const isMutation = method !== 'GET';

    const response = await fetch(endpoint, {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            // MANDATORY on every mutating call — DRF SessionAuthentication
            // rejects an authenticated POST/PUT without it (see transport.js).
            ...(isMutation ? getCsrfHeaders() : {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });

    let payload = null;
    let parseError = null;
    try {
        payload = await response.json();
    } catch (error) {
        parseError = error;
    }

    if (!response.ok) {
        const envelope = payload && typeof payload === 'object' ? payload : {};

        // EXPIRED SESSION ≠ DOMAIN DENIAL. Both arrive as 403, and collapsing
        // them was a real dead end: DRF's SessionAuthentication answers an
        // expired session with its own generic `{"detail": "..."}` shape, which
        // has no `error_code`. Turning that into SeasonApiError('HTTP_403')
        // left SeasonTeamPage mounted under stale cached auth, so every Retry
        // re-issued the same request and failed identically — the user could
        // not get back to login without manually clearing storage.
        //
        // THE DISCRIMINATOR IS `error_code`, not the status. Every season
        // failure goes through SeasonAPIError.to_response_data(), which ALWAYS
        // emits {error_code, message, context} — see
        // backend/football/season/error_codes.py. So:
        //
        //   403 WITH  error_code → a domain denial the user can act on
        //                          (SEASON_FEATURE_DISABLED, SEASON_NOT_COMMISSIONER…)
        //                          → keep it, the screens render it verbatim.
        //   401, or 403 WITHOUT  → not authenticated at all → hand to the shared
        //                          auth recovery, which clears cached auth and
        //                          returns to login.
        //
        // Matching on status alone would blow away the session every time a
        // non-commissioner pressed the autodraft button, which is a perfectly
        // ordinary "no" — the opposite failure, and worse.
        const isUnauthenticated =
            response.status === 401 ||
            (response.status === 403 && !envelope.error_code);

        if (isUnauthenticated) {
            console.error(
                `🔐 SEASON API ${response.status} from ${method} ${endpoint} — NOT AUTHENTICATED ` +
                '(no season error_code in the body). Clearing cached auth and returning to login.',
                { detail: envelope.detail }
            );
            // Clears cached auth + session keys and redirects to '/'. Returns
            // true when it handled it; we still throw so no caller proceeds as
            // if the request had succeeded during the redirect.
            handleApiAuthError({ status: response.status });
            throw new SeasonApiError({
                errorCode: 'NOT_AUTHENTICATED',
                message:
                    envelope.detail ||
                    `HTTP ${response.status} from ${method} ${endpoint} — session expired or not authenticated.`,
                context: {},
                status: response.status,
                endpoint,
            });
        }

        // Log the envelope as a STRUCTURED object before throwing. The thrown
        // error carries a stringified copy for anywhere that only prints
        // `.message`, but an expandable object in the console is far easier to
        // read for a nested `validation_errors` map — and this fires even if a
        // caller swallows the rejection.
        console.error(
            `❌ SEASON API ${response.status} from ${method} ${endpoint}`,
            {
                error_code: envelope.error_code,
                message: envelope.message,
                context: envelope.context,
                request_body: body,
            }
        );
        throw new SeasonApiError({
            errorCode: envelope.error_code || `HTTP_${response.status}`,
            message:
                envelope.message ||
                `HTTP ${response.status} from ${method} ${endpoint} (no season error envelope in the response body)`,
            context: envelope.context,
            status: response.status,
            endpoint,
        });
    }

    if (payload === null) {
        throw new Error(
            `❌ SEASON API: ${method} ${endpoint} returned ${response.status} with a body that is not JSON: ${
                parseError ? parseError.message : 'empty body'
            }`
        );
    }

    return payload;
}

/**
 * Assert a list-shaped response key exists and is an array.
 *
 * A missing key is a contract break, not "no rows" — collapsing it to [] would
 * render an empty screen that looks exactly like a genuinely empty league.
 */
/**
 * Unwrap a list envelope and assert it really is a list.
 *
 * ⚠️ RETURN-SHAPE CONTRACT: every wrapper that calls this returns the **ARRAY
 * ITSELF**, not the `{ <key>: [...] }` envelope. Callers must therefore do
 * `rows.map(...)`, never `rows.<key>.map(...)` — the latter is `undefined` and
 * throws at runtime, which is exactly how season GEAR shipped broken once.
 *
 * Wrappers with this contract: getSeasonAvailableLeagues, getSeasonLeagueTemplates,
 * getSeasonStandings, getSeasonSchedule, getSeasonActivity, getSeasonRoster.
 * (getSeasonDashboard/getSeasonRules/getSeasonLineup return OBJECTS — those are
 * accessed by field.)
 */
function requireArray(payload, key, endpoint) {
    if (!Array.isArray(payload[key])) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: expected "${key}" array from ${endpoint}, got ${JSON.stringify(
                payload[key]
            )}`
        );
    }
    return payload[key];
}

// ---------------------------------------------------------------------------
// Discovery / creation / membership
// ---------------------------------------------------------------------------

/**
 * Season leagues currently FORMING, public, with a free slot, that the caller
 * has not already joined.
 *
 * Rows: { league_id, name, team_count, teams_joined, season_year,
 *         regular_season_start_week, league_type }
 */
export async function getSeasonAvailableLeagues() {
    const payload = await seasonRequest('season-leagues/available/');
    return requireArray(payload, 'leagues', 'GET v2/season-leagues/available/');
}

/**
 * Active season league templates — the shapes a new season league can be
 * created from.
 *
 * Rows: { template_id, name, team_count, season_year,
 *         regular_season_start_week, regular_season_end_week,
 *         playoff_start_week, championship_week }
 */
export async function getSeasonLeagueTemplates() {
    const payload = await seasonRequest('season-league-templates/');
    return requireArray(payload, 'templates', 'GET v2/season-league-templates/');
}

/**
 * Backend contract: `SeasonLeagueCreationService.create_league()` enforces
 * `1 <= len(league_name) <= 100`.
 */
export const SEASON_LEAGUE_NAME_MAX_LENGTH = 100;

const SEASON_LEAGUE_NAME_SUFFIX = "'s Campaign";

/**
 * Derive a season league name from a team name, GUARANTEED to satisfy the
 * backend's 1–100 character rule.
 *
 * Why this exists: `Team.name` allows 100 characters, so a perfectly legal
 * 90–100 character team name produced a 101–111 character league name once
 * "'s Campaign" was appended. The team could JOIN a campaign but could never
 * CREATE one — a valid input that always failed, with a 400 the user could do
 * nothing about because the league name was never shown to them.
 *
 * Truncation (not rejection) is right here because the user never chose this
 * string: they named a TEAM. Blocking a legal team name over a derived value
 * they cannot see or edit would punish them for our formatting choice. The
 * ellipsis makes the shortening visible rather than silent.
 */
export function deriveSeasonLeagueName(teamName) {
    if (typeof teamName !== 'string' || !teamName.trim()) {
        throw new Error(
            `❌ INVALID PARAMETER: deriveSeasonLeagueName requires a non-empty team name, got ${JSON.stringify(
                teamName
            )}`
        );
    }
    const trimmed = teamName.trim();
    const candidate = `${trimmed}${SEASON_LEAGUE_NAME_SUFFIX}`;
    if (candidate.length <= SEASON_LEAGUE_NAME_MAX_LENGTH) {
        return candidate;
    }
    // Reserve room for the suffix and one ellipsis character.
    const room = SEASON_LEAGUE_NAME_MAX_LENGTH - SEASON_LEAGUE_NAME_SUFFIX.length - 1;
    return `${trimmed.slice(0, room)}…${SEASON_LEAGUE_NAME_SUFFIX}`;
}

/**
 * Create a season league from a template. Returns the dashboard payload plus
 * `team_id` (the creator's team) and, for PRIVATE leagues, `league_code`.
 *
 * `templateId` is REQUIRED by the backend — unlike the weekly private-league
 * flow there is no "resolve the current-week default" branch, so there is
 * nothing sane to omit. A caller without a real template id has a bug.
 *
 * ⚠️ NO `known_as` / `hide_known_as`. SeasonLeagueCreationService creates the
 * team as `Team.objects.create(league=…, owner=…, name=creator_team_name)` —
 * the weekly naming fields are never read or written. Do NOT add them to this
 * body hoping they stick; they are silently dropped. Callers must pass
 * `supportsKnownAs: false` to TeamNameModal so those inputs are not collected
 * at all. If season teams should support a profile name, that is a BACKEND
 * change to the creation/join services, not something this wrapper can fake.
 *
 * The backend enforces the shared Weekly + Season account league cap under a
 * UserProfile row lock. A capped account receives the stable
 * SEASON_LEAGUE_LIMIT_REACHED error envelope with real count context. Do not
 * duplicate that authority with a client-side pre-check; render the server
 * response instead. See AIDocs/Final/backend/season-league-foundation-tdd.md.
 */
export async function createSeasonLeague({
    templateId,
    leagueName,
    teamName,
    leagueType,
    leagueCode,
}) {
    if (!Number.isInteger(Number(templateId)) || Number(templateId) <= 0) {
        throw new Error(
            `❌ INVALID PARAMETER: createSeasonLeague requires a positive integer template_id from real backend data, got ${JSON.stringify(
                templateId
            )}`
        );
    }
    if (leagueType !== 'PUBLIC' && leagueType !== 'PRIVATE') {
        throw new Error(
            `❌ INVALID PARAMETER: createSeasonLeague league_type must be PUBLIC or PRIVATE, got ${JSON.stringify(
                leagueType
            )}`
        );
    }
    // Belt AND braces: callers should build this with deriveSeasonLeagueName(),
    // but a future caller that hand-rolls the string must fail HERE — with the
    // real length in the message — rather than as an opaque 400 from the server.
    if (
        typeof leagueName !== 'string' ||
        !leagueName.trim() ||
        leagueName.length > SEASON_LEAGUE_NAME_MAX_LENGTH
    ) {
        throw new Error(
            `❌ INVALID PARAMETER: createSeasonLeague league_name must be 1–${SEASON_LEAGUE_NAME_MAX_LENGTH} characters ` +
                `(backend contract), got length=${
                    typeof leagueName === 'string' ? leagueName.length : 'n/a'
                }: ${JSON.stringify(leagueName)}. Build it with deriveSeasonLeagueName().`
        );
    }

    const body = {
        template_id: Number(templateId),
        league_name: leagueName,
        team_name: teamName,
        league_type: leagueType,
    };
    // Only sent when the caller genuinely supplied one — the backend generates
    // a unique code itself for a PRIVATE league when the key is absent.
    if (leagueCode) {
        body.league_code = leagueCode;
    }

    const payload = await seasonRequest('season-leagues/', { method: 'POST', body });

    if (!payload.team_id || !payload.league || !payload.league.id) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: create season league returned no team_id/league.id: ${JSON.stringify(
                payload
            )}`
        );
    }
    return payload;
}

/**
 * Join a FORMING season league. Returns { team_id, league_id }.
 * `leagueCode` is required by the backend for PRIVATE leagues only.
 *
 * ⚠️ Same naming-field limitation as createSeasonLeague(): SeasonLeagueJoinService
 * does `Team.objects.create(league=league, owner=user, name=team_name)` and
 * accepts no `known_as`/`hide_known_as`.
 *
 * The backend enforces the same shared account league cap transactionally for
 * explicit joins and returns SEASON_LEAGUE_LIMIT_REACHED when full. This client
 * must surface that server error rather than attempting a local cap decision.
 * See AIDocs/Final/backend/season-league-foundation-tdd.md.
 */
export async function joinSeasonLeague(leagueId, { teamName, leagueCode } = {}) {
    const body = { team_name: teamName };
    if (leagueCode) {
        body.league_code = leagueCode;
    }
    const payload = await seasonRequest(`season-leagues/${leagueId}/join/`, {
        method: 'POST',
        body,
    });
    if (!payload.team_id) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: join season league ${leagueId} returned no team_id: ${JSON.stringify(
                payload
            )}`
        );
    }
    return payload;
}

// ---------------------------------------------------------------------------
// League reads
// ---------------------------------------------------------------------------

/**
 * League dashboard — the single source of truth for lifecycle state and for
 * what the caller is allowed to do right now.
 *
 * { league: {id, mode, status, name},
 *   current_week: {week, status, lock_at, rules_version} | null,
 *   my_team: {id, name, bracket, eliminated, record:{wins,losses,ties}} | null,
 *   available_actions: {can_edit_lineup, can_open_pack,
 *                       packs_remaining_this_week, can_start_league} }
 *
 * `available_actions` is SERVER-DERIVED. `can_edit_lineup` covers both an open
 * scheduled week and a draft-complete manager's preseason lineup while the
 * league is FORMING/DRAFTING. Never recompute it client-side from lifecycle
 * status — the server owns that decision and the two must not diverge.
 */
export async function getSeasonDashboard(leagueId) {
    const payload = await seasonRequest(`season-leagues/${leagueId}/`);
    if (!payload.league || !payload.available_actions) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: dashboard for league ${leagueId} is missing league/available_actions: ${JSON.stringify(
                payload
            )}`
        );
    }
    return payload;
}

/**
 * The effective rules version — roster/bench capacity and, critically, the
 * `slot_definitions` the lineup editor is built from.
 *
 * @param {number} [week] - when given, the version PINNED to that week (what
 *        that week was/will be scored under). Omit for the currently effective
 *        version. These genuinely differ mid-season; pass the week you are
 *        editing.
 */
export async function getSeasonRules(leagueId, week) {
    const query = week === undefined || week === null ? '' : `?week=${week}`;
    const payload = await seasonRequest(`season-leagues/${leagueId}/rules/${query}`);
    if (!Array.isArray(payload.slot_definitions)) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: rules for league ${leagueId} have no slot_definitions array: ${JSON.stringify(
                payload
            )}`
        );
    }
    return payload;
}

/**
 * Standings. Returns the whole payload, not just the rows: `tiebreak_recipe_applied`
 * is part of the contract and must be shown — a provisional ordering must never
 * be presented as the pinned recipe.
 *
 * CONSUMED BY SeasonStandingsPage. Do NOT let a season league fall through to
 * the weekly `getLeagueStandings()`: that ranks `Team.wins/losses/ties` plus the
 * CURRENT week's WeeklyTeamStats (substituting 0 when absent) on a hardcoded
 * sort, whereas these rows come from SEALED SeasonTeamWeekResult records ordered
 * by the rules version's pinned tiebreak recipe. The weekly endpoint has no
 * guard that rejects a season league, so the mistake renders a plausible,
 * silently wrong table instead of failing.
 */
export async function getSeasonStandings(leagueId) {
    const payload = await seasonRequest(`season-leagues/${leagueId}/standings/`);
    requireArray(payload, 'standings', `GET v2/season-leagues/${leagueId}/standings/`);
    return payload;
}

/**
 * Full schedule. Each row carries `week_status` and `is_provisional`:
 * a FINALIZED week's scores are VISIBLE BUT NOT OFFICIAL — standings only move
 * at SEALED. Any UI printing a score must respect that flag.
 */
export async function getSeasonSchedule(leagueId) {
    const payload = await seasonRequest(`season-leagues/${leagueId}/schedule/`);
    return requireArray(payload, 'schedule', `GET v2/season-leagues/${leagueId}/schedule/`);
}

/** League activity feed (newest first, capped at 100 by the backend). */
export async function getSeasonActivity(leagueId) {
    const payload = await seasonRequest(`season-leagues/${leagueId}/activity/`);
    return requireArray(payload, 'activity', `GET v2/season-leagues/${leagueId}/activity/`);
}

/**
 * Commissioner: start the league early / on time. 201 on success.
 * Only offered when `available_actions.can_start_league` is true — the backend
 * re-checks commissioner identity and membership size regardless.
 */
export async function startSeasonLeague(leagueId) {
    return seasonRequest(`season-leagues/${leagueId}/commissioner/start/`, { method: 'POST' });
}

/**
 * Commissioner: autodraft every team that hasn't finished drafting, then
 * complete the regular-season handoff. THE RECOVERY PATH FOR A STALLED DRAFT.
 *
 * Why this must exist in the UI: the draft→regular-season handoff waits for
 * EVERY team to finish. One manager who never drafts blocks the whole league
 * indefinitely, and no automatic timeout resolves it. This endpoint is the only
 * way out.
 *
 * It is also the documented retry for a failed launch — if a previous handoff
 * died after the last team's completion committed, calling this again re-runs
 * `complete_regular_season_handoff()` without re-drafting anyone. Safe to call
 * when nothing is outstanding: it processes zero teams and simply retries the
 * handoff.
 *
 * Backend requires: caller IS the commissioner (403 `SEASON_NOT_COMMISSIONER`)
 * and the league is DRAFTING (409 `SEASON_DRAFT_NOT_ELIGIBLE`).
 *
 * PARTIAL FAILURE IS A FAILURE. If any team's autodraft fails the backend
 * returns 409 `SEASON_AUTODRAFT_FAILED` with a per-team `context.failures`
 * array and NO success payload — it never reports partial success. Surface
 * that context; it names which teams could not be drafted and why.
 *
 * @returns {Promise<{success: boolean, league_id: number, teams_processed: number, team_results: Array}>}
 */
export async function autodraftSeasonStragglers(leagueId) {
    return seasonRequest(
        `season-leagues/${leagueId}/commissioner/autodraft-stragglers/`,
        { method: 'POST' }
    );
}

// ---------------------------------------------------------------------------
// Team reads + lineup writes
// ---------------------------------------------------------------------------

/**
 * ACTIVE season roster.
 *
 * ⚠️ RETURNS THE ARRAY, not `{ roster: [...] }` — call `.map()` on the result
 * directly. Reaching for `.roster` on it yields `undefined` and throws.
 *
 * Rows: { league_player_id, character_sheet_id, nfl_player_id, nfl_player_name,
 *         nflverse_id, gsis_id, source_player_id, position, roster_status,
 *         acquired_via }
 *
 * `character_sheet_id` is the CharacterSheet PK the gear endpoints require — it
 * is NOT interchangeable with `league_player_id` (different tables). Never
 * substitute one for the other.
 *
 * NOTE — no scores, projections or matchup data are returned by this endpoint.
 * Consumers must render missing numbers as unknown ("—"), never as 0.
 */
export async function getSeasonRoster(teamId) {
    const payload = await seasonRequest(`season-teams/${teamId}/roster/`);
    return requireArray(payload, 'roster', `GET v2/season-teams/${teamId}/roster/`);
}

/**
 * The persisted lineup for one week.
 *
 * { lineup_id, season_week, is_complete, submitted_at, locked_at,
 *   players: [{league_player_id, slot_code, is_starter}], warnings: [] }
 *
 * `lineup_id: null` means "no lineup saved yet" — a real, expected state, not
 * an error. Owner-only before lock; member-visible after.
 */
export async function getSeasonLineup(teamId, week) {
    if (week === undefined || week === null) {
        throw new Error(
            `❌ INVALID PARAMETER: getSeasonLineup requires a week for team ${teamId}, got ${JSON.stringify(week)}`
        );
    }
    const payload = await seasonRequest(`season-teams/${teamId}/lineup/?week=${week}`);
    requireArray(payload, 'players', `GET v2/season-teams/${teamId}/lineup/`);
    return payload;
}

/**
 * A draft-complete manager's persisted initial-lineup intent before the
 * all-team draft handoff creates a real Week 1. This is deliberately not a
 * numeric week and carries `is_preseason: true` in its otherwise lineup-shaped
 * response. It has no submission or lock state.
 */
export async function getSeasonPreseasonLineup(teamId) {
    const payload = await seasonRequest(`season-teams/${teamId}/lineup/?preseason=true`);
    requireArray(payload, 'players', `GET v2/season-teams/${teamId}/lineup/?preseason=true`);
    if (payload.is_preseason !== true || payload.season_week !== null) {
        throw new Error(
            `❌ INVALID SEASON API RESPONSE: preseason lineup for team ${teamId} must have ` +
                `is_preseason=true and season_week=null, got ${JSON.stringify(payload)}`
        );
    }
    return payload;
}

/**
 * FULL REPLACEMENT of the starter map — this is not a patch.
 *
 * The backend requires EVERY required slot to be present in one call and
 * derives the bench itself as (active roster − starters). A partial array is
 * rejected with SEASON_LINEUP_SLOT_INELIGIBLE naming the missing slots, so a
 * caller must assemble the complete map before calling.
 *
 * @param {Array<{slot_code: string, league_player_id: number}>} starters
 */
export async function saveSeasonLineup(teamId, week, starters) {
    if (!Array.isArray(starters)) {
        throw new Error(
            `❌ INVALID PARAMETER: saveSeasonLineup starters must be an array, got ${JSON.stringify(starters)}`
        );
    }
    return seasonRequest(`season-teams/${teamId}/lineup/?week=${week}`, {
        method: 'PUT',
        body: { starters },
    });
}

/**
 * Full replacement of a preseason starter map. The server derives bench rows,
 * rejects incomplete drafts, and refuses this endpoint as soon as a real Week 1
 * exists; callers must use saveSeasonLineup() for scheduled weeks.
 */
export async function saveSeasonPreseasonLineup(teamId, starters) {
    if (!Array.isArray(starters)) {
        throw new Error(
            `❌ INVALID PARAMETER: saveSeasonPreseasonLineup starters must be an array, got ${JSON.stringify(starters)}`
        );
    }
    return seasonRequest(`season-teams/${teamId}/lineup/?preseason=true`, {
        method: 'PUT',
        body: { starters },
    });
}

/**
 * Stamp the saved lineup as submitted. NOT final — edits stay legal while the
 * week is LINEUPS_OPEN (and clear the stamp). Only the week lock is
 * irreversible.
 */
export async function submitSeasonLineup(teamId, week) {
    return seasonRequest(`season-teams/${teamId}/lineup/submit/?week=${week}`, {
        method: 'POST',
    });
}
